import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { message } from 'antd';

// 错误处理方案： 错误类型
enum _ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// 与后端约定的响应数据格式
interface ResponseStructure {
  code: number;
  data: any;
  message?: string;
}

// 未登录时仍允许访问的接口（登录、微信回调等）
const PUBLIC_URL_PATTERNS = [
  '/api/auth/login',
  '/api/auth/logout',
  '/api/wechat/',
];
const isPublicUrl = (url?: string) =>
  !!url && PUBLIC_URL_PATTERNS.some((p) => url.includes(p));

// 退出登录后被拦截器短路的请求使用的错误名，便于在 errorHandler 中静默
const LOGGED_OUT_ABORT = 'LoggedOutAbort';

const normalizeErrorMessage = (msg: unknown): string | undefined => {
  if (Array.isArray(msg)) return msg.filter(Boolean).join('，');
  if (typeof msg === 'string' && msg.trim()) return msg;
  return undefined;
};

const getResponseErrorMessage = (error: any) =>
  normalizeErrorMessage(error?.info?.message) ||
  normalizeErrorMessage(error?.data?.message) ||
  normalizeErrorMessage(error?.response?.data?.message) ||
  normalizeErrorMessage(error?.response?.message);

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const {
        code,
        data,
        message: errorMessage,
      } = res as unknown as ResponseStructure;
      if (code !== undefined && code !== 200) {
        const error: any = new Error(errorMessage);
        error.name = 'BizError';
        error.info = { code, message: errorMessage, data };
        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;
      // 已退出登录时被拦截器短路的请求：静默忽略，不提示、不跳转
      if (error?.name === LOGGED_OUT_ABORT) return;
      if (error.name === 'BizError') {
        message.error(getResponseErrorMessage(error) || '请求失败');
      } else if (error.response) {
        const status = error.response.status;
        const resMsg = getResponseErrorMessage(error);
        if (status === 401) {
          // 登录请求没有 token 是正常状态，不能被下面的“已退出登录静默”逻辑吞掉。
          if (
            window.location.pathname === '/user/login' ||
            error.config?.url?.includes('/api/auth/login')
          ) {
            message.error(resMsg || '用户名或密码错误');
            return;
          }
          // 本地已无 token：通常是退出登录后到达的 stale 响应，静默丢弃
          if (!localStorage.getItem('token')) return;
          message.error(resMsg || '登录已过期，请重新登录');
          localStorage.removeItem('token');
          window.location.href = '/user/login';
        } else if (status === 403) {
          message.error(resMsg || '您没有权限执行此操作');
        } else if (status === 404) {
          message.error(resMsg || '请求的资源不存在');
        } else if (status >= 500) {
          message.error(resMsg || '服务器错误，请稍后重试');
        } else {
          message.error(resMsg || `请求失败 (${status})`);
        }
      } else if (error.request) {
        message.error('网络连接异常，请检查网络后重试');
      } else {
        message.error('请求出错，请重试');
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (config: RequestOptions) => {
      const token = localStorage.getItem('token');
      // 已退出登录（无 token）时，阻止继续向受保护接口发请求，避免出现退出后 401 的链路
      if (!token && !isPublicUrl(config.url)) {
        const err: any = new Error('User logged out, request aborted');
        err.name = LOGGED_OUT_ABORT;
        throw err;
      }
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
  ],

  // 响应拦截器：仅做数据透传，错误统一由 errorThrower -> errorHandler 链路处理
  responseInterceptors: [
    (response) => {
      return response;
    },
  ],
};
